/**
 * Order Summary Tab — Feb 25, 2026 (P1, refresh).
 * TITLEFLOW-ORDER-SUMMARY-UI-002 (Oct 2026) — premium wide layout.
 *
 * Visually mirrors the premium `OrderSummaryPopup` (ORDER-SUMMARY-PREMIUM-001)
 * so Search Processors / Tax Processors / TLs / QC / Order Desk get the
 * SAME card experience whether they open Summary as a popup or as the
 * default tab — but uses ~96% of the page width.
 *
 * Sections:
 *   • Blue→violet gradient "Order Summary" header with artwork
 *   • LEFT col: Order ID + Priority chip · Client Code / Client Product /
 *               Product ID (/ Loan ID) · Property + County/State ·
 *               Borrower/Owner (+ Seller)
 *   • RIGHT col: Status + Assignee · Ordered + Loan/Transfer Date ·
 *               QC context · Turnaround · Parcel · Research Links ·
 *               Quick Priority controls (Admin / TLs)
 *   • Notes (collapsible) · Mail-Away recovery · Tax email requests
 *   • Recent Activity (reused from OrderSummaryPopup)
 *   • Footer CTA: "Research Log" / "Tax Callback Sheet" → /fulfillment
 *
 * Billing-FREE — no invoiced amounts, line items, or profit metrics.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Building2, Briefcase, Tag, MapPin, UserRound, Users, Box, CalendarDays, CalendarClock, Clock, Hash, ChevronRight, ClipboardCheck } from 'lucide-react';
import { calculateDaysLate } from '../../../utils/daysLateCalculator';
import { isTaxProduct, isSearchProduct, getCallbackSheetLabel } from '../../../utils/orderHelpers';
import {
  formatStreetAddress, formatCity, formatState, formatCounty,
} from '../../../utils/textFormat';
import { normalizeBorrowerName } from '../../../utils/nameNormalizer';
import { formatDateOnlyET, formatDateOnly } from '../../../utils/formatters';
import { CopyIconButton } from '../../ui/copy-field';
import { ContractCodeChip, productDisplayText } from '../ProductWithCode';
import { RecentActivitySection } from '../../OrderSummaryPopup';
import TaxEmailRequestsSection from '../TaxEmailRequestsSection';
import ResearchLinksCard from '../../search/ResearchLinksCard';
import { getUserFromToken } from '../../../utils/adminApi';
import { MailAwayRecoveryCard } from '../ReturnToMailAwayDialog';
import { SummaryPriorityBar, canEditSummaryPriority } from './SummaryPriorityBar';

// RL-HIDE-TAX-001: tax roles never see Research Links in Order Summary (same rule as the popup).
const RESEARCH_LINKS_HIDDEN_ROLES = ['tax_processor', 'processor', 'tax_search_team_leader', 'tax_team_leader'];

/* presentational helpers (UI only) — same tokens as OrderSummaryPopup */
const T = 'transition-[box-shadow,border-color,background-color,transform,color] duration-200 ease-out';
const SCard = ({ children, className = '', testId }) => (
  <div className={`bg-white border border-slate-200 rounded-xl px-3.5 py-2.5 shadow-[0_1px_2px_rgba(15,23,42,0.04)] animate-in fade-in-0 slide-in-from-bottom-1 duration-200 ${T} hover:shadow-[0_6px_20px_-10px_rgba(15,23,42,0.25)] hover:border-slate-300 ${className}`} data-testid={testId}>
    {children}
  </div>
);
const SIcon = ({ icon: Icon, tone = 'blue' }) => {
  const tones = { blue: 'bg-blue-50 text-[#2563eb]', violet: 'bg-violet-50 text-violet-600', emerald: 'bg-emerald-50 text-emerald-600', rose: 'bg-rose-50 text-rose-500', amber: 'bg-amber-50 text-amber-600', slate: 'bg-slate-100 text-slate-500', indigo: 'bg-indigo-50 text-indigo-600' };
  return <span className={`h-9 w-9 rounded-xl grid place-items-center shrink-0 ${tones[tone] || tones.blue}`}><Icon className="w-[17px] h-[17px]" aria-hidden="true" /></span>;
};
const SLabel = ({ children, className = '' }) => <span className={`block text-[10.5px] font-semibold uppercase tracking-[0.08em] text-slate-500 leading-tight mb-0.5 ${className}`}>{children}</span>;
const SValue = ({ children, size = 'md', className = '', ...rest }) => (
  <span {...rest} className={`${size === 'lg' ? 'text-[16px] font-bold tracking-tight' : 'text-[13.5px] font-semibold'} text-slate-900 break-words min-w-0 leading-snug ${className}`}>{children}</span>
);

const getStatusStyle = (status) => {
  switch (status?.toLowerCase()) {
    case 'complete':
    case 'completed':
      return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    case 'in_progress':
    case 'in progress':
      return 'bg-amber-50 text-amber-700 border-amber-200';
    case 'open':
      return 'bg-indigo-50 text-indigo-700 border-indigo-200';
    case 'clarification':
      return 'bg-orange-50 text-orange-700 border-orange-200';
    case 'mail_away':
    case 'mailaway':
      return 'bg-cyan-50 text-cyan-700 border-cyan-200';
    default:
      return 'bg-gray-50 text-gray-700 border-gray-200';
  }
};

const getPriorityStyle = (priority) => {
  switch (priority?.toLowerCase()) {
    case 'rush':   return 'bg-blue-100 text-blue-700 border-blue-300';
    case 'urgent': return 'bg-red-100 text-red-700 border-red-300';
    case 'high':   return 'bg-orange-100 text-orange-700 border-orange-300';
    case 'normal':
    default:       return 'bg-amber-50 text-amber-700 border-amber-200';
  }
};

const getPriorityDot = (priority) => {
  switch (priority?.toLowerCase()) {
    case 'urgent': return 'bg-red-500';
    case 'rush':   return 'bg-blue-600';
    case 'high':   return 'bg-orange-500';
    default:       return 'bg-amber-400';
  }
};

// loan_date is a calendar date (no time/TZ) — format directly to avoid
// the ET shift that formatDateShortET applies (would render "Apr 15"
// instead of "Apr 16" because midnight UTC is 8pm ET the previous day).
const formatLoanDate = (raw) => {
  if (!raw) return 'N/A';
  try {
    const iso = typeof raw === 'string' ? raw : (raw?.toISOString?.() || String(raw));
    const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m) return 'N/A';
    const [, y, mo, d] = m;
    return formatDateOnly(`${y}-${mo}-${d}`);
  } catch {
    return 'N/A';
  }
};

const SummaryTab = ({ orderData, onOrderUpdate, onRefreshOrder }) => {
  const [activityRefreshKey, setActivityRefreshKey] = useState(0);
  const navigate = useNavigate();
  const currentUser = getUserFromToken();

  if (!orderData) {
    return (
      <div className="bg-white border border-gray-200 rounded-2xl p-8 text-center text-slate-400">
        Loading order summary…
      </div>
    );
  }

  // Field extraction — supports BOTH the order-details API shape
  // (snake_case + nested property/customer) AND the dashboard list
  // shape (camelCase). Mirrors OrderSummaryPopup's resolution logic.
  const orderId = orderData.order_number || orderData.orderNumber || orderData.id;
  const clientProduct = orderData.client_product || orderData.clientProduct || 'N/A';
  const productId = orderData.product_id || orderData.productId
    || orderData.external_task_id || orderData.externalTaskId
    || orderData.task_id || orderData.taskId
    || orderData.external_loan_id || orderData.externalLoanId || 'N/A';

  // May 1, 2026 — Loan ID surface for Search products. See matching note
  // in OrderSummaryPopup.jsx. Source = bulk-upload "Loan ID" column,
  // persisted as `external_loan_id` (preferred) / `loan_id` (legacy).
  // Only displayed when product is Search AND the value exists AND it
  // differs from `productId` (avoids duplicate when bulk loader fell
  // back to loan_id for product_id).
  const _rawLoanId = orderData.external_loan_id || orderData.externalLoanId
                  || orderData.loan_id || orderData.loanId || '';
  const _loanIdNorm = String(_rawLoanId || '').trim();
  const _loanIdIsPlaceholder = ['', 'na', 'n/a', '-', 'null', 'none'].includes(_loanIdNorm.toLowerCase());
  const loanId = _loanIdIsPlaceholder ? '' : _loanIdNorm;
  // Feb 2026 — Pass the ORDER OBJECT (not the display-name string) so the
  // canonical `workflow_type === 'TITLE_SEARCH'` classification wins over
  // fragile display-name matching. See OrderSummaryPopup.jsx for the full RCA.
  const showLoanId = isSearchProduct(orderData)
                  && Boolean(loanId)
                  && loanId !== productId;

  // Feb 26, 2026 — Loan Amount surface (all products). Source = bulk-upload
  // "Loan Amount" column, persisted as `loan_amount` (float) on the order.
  // Hidden entirely when the value is missing/blank to avoid clutter on
  // legacy/manual orders. `0` is treated as a valid stored value.
  const _rawLoanAmount = orderData.loan_amount ?? orderData.loanAmount;
  const _loanAmountNum = (() => {
    if (_rawLoanAmount === null || _rawLoanAmount === undefined || _rawLoanAmount === '') return null;
    const n = typeof _rawLoanAmount === 'string'
      ? parseFloat(_rawLoanAmount.replace(/[^0-9.-]/g, ''))
      : Number(_rawLoanAmount);
    return Number.isFinite(n) ? n : null;
  })();
  const loanAmountDisplay = _loanAmountNum === null
    ? ''
    : new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(_loanAmountNum);
  const showLoanAmount = Boolean(loanAmountDisplay);
  const clientCode = orderData.client_code || orderData.clientCode || 'N/A';
  const priority = orderData.priority || 'normal';
  const status = orderData.status || 'N/A';

  const _rawCity  = orderData.property_city || orderData.propertyCity || orderData.property?.city || orderData.city || '';
  const _rawState = orderData.property_state || orderData.propertyState || orderData.property?.state || orderData.state || '';
  const propertyAddress = formatStreetAddress(
    orderData.property_address_full || orderData.property_address
    || orderData.propertyAddress || orderData.property?.address
    || orderData.fullAddress
  );
  const propertyCity  = _rawCity  ? formatCity(_rawCity)   : '';
  const propertyState = _rawState ? formatState(_rawState) : '';
  const propertyZip = orderData.property_zip_code || orderData.property_zip
    || orderData.propertyZip || orderData.property?.zip_code
    || orderData.zipCode || orderData.zip || '';
  const county = formatCounty(
    orderData.property_county || orderData.county
    || orderData.propertyCounty || orderData.property?.county
  );
  const parcel = orderData.parcel || orderData.parcel_apn || orderData.parcelApn || orderData.certificate_data?.parcel || '';
  const fullAddress = `${propertyAddress}, ${[propertyCity, propertyState, propertyZip].filter(Boolean).join(', ')}`;

  // Feb 2026 — UI-side defensive normalization to match Tax Cert PDF.
  // Stored value preserved on disk; render shows canonical title-case.
  const _rawBorrower = orderData.borrower_name || orderData.borrowerName
    || orderData.customer?.borrower_name || orderData.borrower || '';
  const borrowerName = _rawBorrower ? (normalizeBorrowerName(_rawBorrower) || _rawBorrower) : 'N/A';
  // SELLER-NAME-001: data-driven — row rendered only when a seller was captured
  const _rawSeller = (orderData.seller_name || '').trim();
  const sellerName = _rawSeller ? (normalizeBorrowerName(_rawSeller) || _rawSeller) : '';

  const orderedDate = orderData.created_at || orderData.createdAt || orderData.created_date || orderData.orderedOn;
  const loanTransferDate = orderData.loan_date || orderData.loanDate;
  const _comps = orderData.components;
  const _isDualMls = !!(_comps && _comps.tax && _comps.mls);
  const assigneeName = _isDualMls
    ? ((_comps.tax?.assignee_name || _comps.mls?.assignee_name)
        ? `T: ${_comps.tax?.assignee_name || '—'} | M: ${_comps.mls?.assignee_name || '—'}`
        : 'Unassigned')
    : (orderData.assignee_name || orderData.assigneeName || orderData.assignee || 'Unassigned');
  const notes = (typeof orderData.notes === 'string' && orderData.notes)
    || orderData.order_notes || orderData.orderNotes || '';

  const daysLateInfo = calculateDaysLate(orderData);

  // Days elapsed (calendar-day based, mirrors popup behaviour)
  const calculateDaysElapsed = () => {
    const createdDate = orderData.created_at || orderData.createdAt || orderData.created_date || orderData.orderedOn;
    if (!createdDate) return null;
    try {
      const created = new Date(createdDate);
      if (isNaN(created.getTime())) return null;
      const now = new Date();
      const createdDay = new Date(created.getFullYear(), created.getMonth(), created.getDate());
      const todayDay   = new Date(now.getFullYear(),     now.getMonth(),     now.getDate());
      const diffTime = todayDay.getTime() - createdDay.getTime();
      return Math.max(0, Math.round(diffTime / (1000 * 60 * 60 * 24)));
    } catch { return null; }
  };

  const calculateDaysRemaining = () => {
    const dueDate = orderData.due_date || orderData.dueDate || orderData.dueBy;
    if (!dueDate) return null;
    try {
      const due = new Date(dueDate);
      const diffTime = due - new Date();
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    } catch { return null; }
  };

  const daysElapsed   = calculateDaysElapsed();
  const daysRemaining = calculateDaysRemaining();
  const metricCount = [daysElapsed !== null, daysRemaining !== null, !!daysLateInfo].filter(Boolean).length;

  // QC Review context — additive, only shown when order is in QC review state.
  const isQCReview = (orderData.status || '').toLowerCase() === 'qc_review';
  const qcSubmittedByName = orderData.qc_submitted_by_name || orderData.qc_submitted_by_full_name || null;
  const qcSubmittedAt = orderData.qc_submitted_at || null;
  const lastModifiedAt = orderData.updated_at || null;

  // RL-HIDE-TAX-001 — identical visibility rule to OrderSummaryPopup.
  const _rlRole = currentUser?.role;
  const showResearchLinks = Boolean(orderData?.property_county)
    && !RESEARCH_LINKS_HIDDEN_ROLES.includes(_rlRole)
    && (_rlRole !== 'admin' || isSearchProduct(orderData));
  const canEditPriority = canEditSummaryPriority(currentUser?.role);

  const handleOpenFulfillment = () => {
    navigate(`/orders/${orderData.id}/fulfillment`);
  };

  return (
    <div className="w-[96%] max-w-[1800px] mx-auto py-3" data-testid="order-summary-tab-container">
      <div
        className="bg-slate-50 border border-slate-200 overflow-hidden rounded-2xl animate-in fade-in-0 slide-in-from-bottom-1 duration-200"
        style={{ boxShadow: '0 18px 45px -18px rgba(15, 23, 42, 0.25)' }}
        data-testid="order-summary-tab"
      >
        {/* Header — blue → violet premium band with artwork */}
        <div className="relative px-6 py-4 overflow-hidden min-h-[96px]" style={{ background: 'linear-gradient(100deg, #1d4ed8 0%, #2563eb 45%, #4f46e5 75%, #7c3aed 100%)' }} data-testid="summary-header">
          <div aria-hidden="true" className="pointer-events-none absolute -right-10 -top-16 h-56 w-56 rounded-full bg-white/15 blur-3xl" />
          <div aria-hidden="true" className="pointer-events-none absolute right-1/3 -bottom-24 h-48 w-96 rounded-full bg-indigo-300/25 blur-3xl" />
          <img
            src="/assets/order-summary-clipboard.png"
            alt=""
            aria-hidden="true"
            draggable="false"
            className="pointer-events-none select-none absolute right-16 -top-2 h-[140px] w-auto opacity-90 hidden md:block"
            style={{ WebkitMaskImage: 'linear-gradient(to bottom, rgba(0,0,0,1) 55%, rgba(0,0,0,0) 100%)', maskImage: 'linear-gradient(to bottom, rgba(0,0,0,1) 55%, rgba(0,0,0,0) 100%)', filter: 'drop-shadow(0 10px 18px rgba(15,23,42,0.25))' }}
            data-testid="summary-header-art"
          />
          <div className="relative flex items-center gap-3.5 min-w-0">
            <span className="h-12 w-12 rounded-2xl bg-white text-[#2563eb] grid place-items-center shadow-lg shadow-blue-900/20 shrink-0"><FileText className="w-6 h-6" aria-hidden="true" /></span>
            <div className="min-w-0">
              <h3 className="text-white font-bold tracking-tight leading-tight text-[22px]">Order Summary</h3>
              <p className="text-blue-100 text-[13.5px] mt-0.5">Complete overview of this order and recent activity.</p>
            </div>
          </div>
        </div>

        {/* Body — two balanced columns */}
        <div className="px-5 pt-3.5 pb-2">
          <div className="grid grid-cols-1 lg:grid-cols-[1.15fr_1fr] gap-3">
            {/* LEFT COLUMN */}
            <div className="space-y-2.5">
              <SCard testId="summary-identity-card">
                <div className="flex items-center gap-3.5 flex-wrap">
                  <SIcon icon={FileText} tone="blue" />
                  <div className="min-w-0 flex-1">
                    <SLabel>Order ID</SLabel>
                    <div className="flex items-center gap-2 flex-wrap">
                      <SValue size="lg" className="break-all" data-testid="summary-order-number">{orderId}</SValue>
                      <CopyIconButton value={orderId} label="Order ID" size="sm" />
                    </div>
                  </div>
                  <span
                    className={`inline-flex items-center gap-1.5 h-7 px-3 rounded-full border font-semibold uppercase tracking-wide text-[11px] ${getPriorityStyle(priority)} ${T}`}
                    data-testid="summary-priority"
                  >
                    <span className={`h-2 w-2 rounded-full ${getPriorityDot(priority)}`} /> {priority || 'normal'}
                  </span>
                </div>
                {showLoanAmount && (
                  <div className="mt-2 pt-2 border-t border-slate-100 pl-[50px]" data-testid="summary-loan-amount">
                    <SLabel>Loan Amount</SLabel>
                    <div className="flex items-center gap-1.5">
                      <SValue data-testid="summary-loan-amount-value">{loanAmountDisplay}</SValue>
                      <CopyIconButton value={loanAmountDisplay} label="Loan Amount" size="sm" />
                    </div>
                  </div>
                )}
              </SCard>

              <SCard testId="summary-client-card">
                <div className={`grid gap-3 grid-cols-1 ${showLoanId ? 'sm:grid-cols-2 xl:grid-cols-4' : 'sm:grid-cols-3'}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <SIcon icon={Building2} tone="violet" />
                    <div className="min-w-0">
                      <SLabel>Client Code</SLabel>
                      <div className="flex items-center gap-1.5 min-w-0 flex-wrap">
                        <SValue data-testid="summary-client-code">{clientCode}</SValue>
                        <CopyIconButton value={clientCode} label="Client Code" size="sm" />
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 min-w-0 sm:border-l sm:border-slate-100 sm:pl-3">
                    <SIcon icon={Briefcase} tone="slate" />
                    <div className="min-w-0" title={productDisplayText(clientProduct, orderData.contract_code || orderData.contractCode)}>
                      <SLabel>Client Product</SLabel>
                      <div className="flex items-center gap-1.5 min-w-0 flex-wrap">
                        <SValue data-testid="summary-client-product">{clientProduct}</SValue>
                        <ContractCodeChip code={orderData.contract_code || orderData.contractCode} testId="summary-contract-code" size="xs" />
                        <CopyIconButton value={productDisplayText(clientProduct, orderData.contract_code || orderData.contractCode)} label="Client Product" size="sm" />
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 min-w-0 sm:border-l sm:border-slate-100 sm:pl-3">
                    <SIcon icon={Tag} tone="indigo" />
                    <div className="min-w-0" title={productId}>
                      <SLabel>Product ID</SLabel>
                      <div className="flex items-center gap-1.5 min-w-0">
                        <SValue className="truncate whitespace-nowrap" data-testid="summary-product-id">{productId}</SValue>
                        {productId && productId !== 'N/A' && <CopyIconButton value={productId} label="Product ID" size="sm" />}
                      </div>
                    </div>
                  </div>
                  {showLoanId && (
                    <div className="flex items-center gap-3 min-w-0 sm:border-l sm:border-slate-100 sm:pl-3" data-testid="summary-loan-id">
                      <SIcon icon={Hash} tone="amber" />
                      <div className="min-w-0" title={loanId}>
                        <SLabel>Loan ID</SLabel>
                        <div className="flex items-center gap-1.5 min-w-0">
                          <SValue className="truncate whitespace-nowrap">{loanId}</SValue>
                          <CopyIconButton value={loanId} label="Loan ID" size="sm" />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </SCard>

              <SCard testId="summary-property-card">
                <div className="flex items-start gap-3">
                  <SIcon icon={MapPin} tone="emerald" />
                  <div className="min-w-0 flex-1">
                    <SLabel>Property Address</SLabel>
                    <div className="flex items-start gap-1.5" title={fullAddress}>
                      <SValue data-testid="summary-property-address">
                        {propertyAddress}
                        {(propertyCity || propertyState || propertyZip) && (
                          <span className="text-slate-600 font-medium">, {[propertyCity, propertyState, propertyZip].filter(Boolean).join(', ')}</span>
                        )}
                      </SValue>
                      <CopyIconButton value={fullAddress} label="Full Address" size="sm" />
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 mt-2 pt-2 border-t border-slate-100 pl-[48px]">
                  <div>
                    <SLabel>County</SLabel>
                    <div className="flex items-center gap-1.5">
                      <SValue data-testid="summary-county">{county}</SValue>
                      <CopyIconButton value={county} label="County" size="sm" />
                    </div>
                  </div>
                  <div>
                    <SLabel>State</SLabel>
                    <div className="flex items-center gap-1.5">
                      <SValue data-testid="summary-state">{propertyState || 'N/A'}</SValue>
                      {propertyState && <CopyIconButton value={propertyState} label="State" size="sm" />}
                    </div>
                  </div>
                </div>
              </SCard>

              <SCard testId="summary-borrower-card">
                <div className="flex items-center gap-3">
                  <SIcon icon={UserRound} tone="blue" />
                  <div className="min-w-0 flex-1">
                    <SLabel>Borrower / Owner</SLabel>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <SValue data-testid="summary-borrower-name">{borrowerName}</SValue>
                      <CopyIconButton value={borrowerName} label="Borrower Name" size="sm" />
                    </div>
                  </div>
                </div>
                {sellerName && (
                  <div className="flex items-center gap-3 mt-2 pt-2 border-t border-slate-100" data-testid="summary-seller-row">
                    <SIcon icon={Users} tone="slate" />
                    <div className="min-w-0 flex-1">
                      <SLabel>Seller (Current Owner)</SLabel>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <SValue data-testid="summary-seller-name">{sellerName}</SValue>
                        <CopyIconButton value={sellerName} label="Seller Name" size="sm" />
                      </div>
                    </div>
                  </div>
                )}
              </SCard>
            </div>

            {/* RIGHT COLUMN */}
            <div className="space-y-2.5">
              <SCard testId="summary-status-card">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <SIcon icon={Box} tone="emerald" />
                    <div className="min-w-0">
                      <SLabel>Status</SLabel>
                      <span className={`inline-flex items-center h-7 px-3 rounded-full border font-semibold capitalize text-[12px] ${getStatusStyle(status)} ${T}`} data-testid="summary-status">
                        {status?.replace(/_/g, ' ') || 'N/A'}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 min-w-0 sm:border-l sm:border-slate-100 sm:pl-3">
                    <SIcon icon={UserRound} tone="blue" />
                    <div className="min-w-0">
                      <SLabel>Assignee</SLabel>
                      <SValue className={assigneeName === 'Unassigned' ? 'text-slate-400 font-medium' : ''} data-testid="summary-assignee">{assigneeName}</SValue>
                    </div>
                  </div>
                </div>
              </SCard>

              <SCard testId="summary-dates-card">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <SIcon icon={CalendarDays} tone="rose" />
                    <div className="min-w-0">
                      <SLabel>Ordered Date</SLabel>
                      <SValue data-testid="summary-ordered-date">{orderedDate ? formatDateOnlyET(orderedDate) : 'N/A'}</SValue>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 min-w-0 sm:border-l sm:border-slate-100 sm:pl-3">
                    <SIcon icon={CalendarClock} tone="amber" />
                    <div className="min-w-0">
                      <SLabel>Loan / Transfer Date</SLabel>
                      <SValue data-testid="summary-loan-transfer-date">{formatLoanDate(loanTransferDate)}</SValue>
                    </div>
                  </div>
                </div>
              </SCard>

              {isQCReview && (
                <SCard className="!bg-emerald-50 !border-emerald-200" testId="summary-qc-context">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <SIcon icon={ClipboardCheck} tone="emerald" />
                      <div className="min-w-0">
                        <SLabel className="!text-emerald-700">Submitted To QC By</SLabel>
                        <SValue data-testid="summary-qc-submitted-by">{qcSubmittedByName || '—'}</SValue>
                        {qcSubmittedAt && <span className="block text-[11px] text-slate-500 mt-0.5" data-testid="summary-qc-submitted-at">{formatDateOnlyET(qcSubmittedAt)}</span>}
                      </div>
                    </div>
                    <div className="min-w-0 sm:border-l sm:border-emerald-100 sm:pl-3">
                      <SLabel className="!text-emerald-700">Last Modified</SLabel>
                      <SValue data-testid="summary-qc-last-modified">{lastModifiedAt ? formatDateOnlyET(lastModifiedAt) : '—'}</SValue>
                    </div>
                  </div>
                </SCard>
              )}

              <SCard testId="summary-turnaround-card">
                <div className="flex items-center gap-3 mb-2">
                  <SIcon icon={Clock} tone="violet" />
                  <SLabel className="!mb-0">Turnaround</SLabel>
                </div>
                <div className={`grid gap-3 ${metricCount === 3 ? 'grid-cols-3' : metricCount === 2 ? 'grid-cols-2' : 'grid-cols-1'}`}>
                  {daysElapsed !== null && (
                    <div className={`rounded-xl border border-blue-100 bg-gradient-to-b from-blue-50/80 to-slate-50 px-3 py-2 text-center ${T} hover:shadow-sm`}>
                      <span className="block text-[12px] font-medium text-slate-600">Elapsed</span>
                      <span className="block text-[20px] font-bold text-slate-900 leading-tight mt-0.5" data-testid="summary-elapsed">{daysElapsed}d</span>
                    </div>
                  )}
                  {daysRemaining !== null && (
                    <div className={`rounded-xl border px-3 py-2 text-center ${T} hover:shadow-sm ${daysRemaining < 0 ? 'bg-rose-50 border-rose-200' : daysRemaining <= 2 ? 'bg-amber-50 border-amber-200' : 'bg-slate-50 border-slate-200'}`}>
                      <span className="block text-[12px] font-medium text-slate-600">Remaining</span>
                      <span className={`block text-[20px] font-bold leading-tight mt-0.5 ${daysRemaining < 0 ? 'text-rose-600' : daysRemaining <= 2 ? 'text-amber-600' : 'text-slate-900'}`} data-testid="summary-remaining">
                        {daysRemaining < 0 ? `${Math.abs(daysRemaining)}d late` : `${daysRemaining}d`}
                      </span>
                    </div>
                  )}
                  {daysLateInfo && (
                    <div className={`rounded-xl border px-3 py-2 text-center ${T} hover:shadow-sm ${daysLateInfo.colorClass}`}>
                      <span className="block text-[12px] font-medium opacity-80">Days Late</span>
                      <span className="block text-[20px] font-bold leading-tight mt-0.5" data-testid="summary-days-late">{daysLateInfo.displayText}</span>
                    </div>
                  )}
                </div>
              </SCard>

              {parcel && (
                <SCard testId="summary-parcel-card">
                  <div className="flex items-center gap-3">
                    <SIcon icon={Hash} tone="slate" />
                    <div className="min-w-0">
                      <SLabel>Parcel #</SLabel>
                      <div className="flex items-center gap-1.5">
                        <SValue className="break-all" data-testid="summary-parcel">{parcel}</SValue>
                        <CopyIconButton value={parcel} label="Parcel ID" size="sm" />
                      </div>
                    </div>
                  </div>
                </SCard>
              )}

              {showResearchLinks && (
                <div data-testid="summary-research-links">
                  <ResearchLinksCard orderId={orderData.id} compact readOnly />
                </div>
              )}

              {canEditPriority && (
                <SummaryPriorityBar
                  orderData={orderData}
                  onUpdate={(patch) => { if (onOrderUpdate) onOrderUpdate(patch); setActivityRefreshKey((k) => k + 1); }}
                />
              )}
            </div>
          </div>

          {/* Notes (collapsible) */}
          {notes && (
            <SCard className="mt-2.5" testId="summary-notes-card">
              <details className="group" data-testid="summary-notes">
                <summary className="cursor-pointer list-none text-[10.5px] font-semibold text-slate-500 uppercase tracking-[0.08em] flex items-center gap-1.5 select-none">
                  <ChevronRight className="w-3.5 h-3.5 transition-transform duration-200 group-open:rotate-90" aria-hidden="true" />
                  Notes
                </summary>
                <div
                  className="mt-2 text-slate-700 text-[13px] bg-slate-50 rounded-lg p-3 max-h-40 overflow-y-auto"
                  style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                >
                  {notes}
                </div>
              </details>
            </SCard>
          )}

          {/* MAILAWAY-RECOVERY-001 — active Mail-Away request on file while the order is not in Mail-Away */}
          <MailAwayRecoveryCard
            order={orderData}
            currentUser={currentUser}
            className="pt-2.5"
            onReturned={() => {
              if (onOrderUpdate) onOrderUpdate({ status: 'mail_away' });
              if (onRefreshOrder) onRefreshOrder();
              setActivityRefreshKey((k) => k + 1);
            }}
          />

          {/* TAX-PENDING-EMAIL-001 — outbound Tax Collector email requests (read + Resend) */}
          {orderData?.tax_email_requests?.length > 0 && (
            <div className="pt-2.5">
              <TaxEmailRequestsSection orderData={orderData} currentUser={currentUser} onUpdate={() => window.location.reload()} />
            </div>
          )}

          {/* Recent Activity — full width within the Summary container */}
          <div className="pt-2.5">
            <RecentActivitySection
              orderId={orderData.id}
              refreshKey={activityRefreshKey}
              onViewAll={() => navigate(`/orders/${orderData.id}/activity`)}
            />
          </div>
        </div>

        {/* Footer — Research Log / Tax Callback Sheet CTA */}
        <div className="bg-white px-5 py-3 flex items-center justify-end gap-2 border-t border-slate-200">
          <button
            onClick={handleOpenFulfillment}
            className={`h-10 px-5 inline-flex items-center gap-2 text-white rounded-xl font-semibold text-[14px] shadow-md ${T} hover:shadow-lg hover:-translate-y-px`}
            style={{ background: 'linear-gradient(to right, #3B82F6, #2563EB)' }}
            data-testid={isTaxProduct(orderData) ? 'summary-tax-callback-btn' : 'summary-research-log-btn'}
          >
            <FileText className="w-4 h-4" aria-hidden="true" />
            {isTaxProduct(orderData) ? getCallbackSheetLabel(orderData) : 'Research Log'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SummaryTab;
